import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  input,
} from '@angular/core';
import { FormControl } from '@angular/forms';

/**
 * Light wrapper around a single form control that renders a label, the
 * projected input, and the first active error from `errorMessages`.
 *
 * Usage:
 *   <vh-form-field [control]="form.controls.email" label="Email"
 *     [errorMessages]="{ required: 'Email is required.', email: 'Invalid email.' }">
 *     <input matInput formControlName="email" />
 *   </vh-form-field>
 */
@Component({
  selector: 'vh-form-field',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
    <label class="vh-field">
      @if (label()) {
        <span class="vh-field__label">
          {{ label() }}
          @if (required()) {
            <span class="vh-field__required" aria-hidden="true">*</span>
          }
        </span>
      }
      <span class="vh-field__input">
        <ng-content />
      </span>
      @if (hint() && !activeError()) {
        <span class="vh-field__hint">{{ hint() }}</span>
      }
      @if (activeError()) {
        <span class="vh-field__error" role="alert">{{ activeError() }}</span>
      }
    </label>
  `,
  styleUrl: './form-field.component.scss',
})
export class VhFormFieldComponent {
  readonly label = input<string>();
  readonly hint = input<string>();
  readonly required = input(false, { transform: booleanAttribute });
  readonly errorMessages = input<Record<string, string>>({});
  readonly control = input<FormControl | null>(null);

  readonly activeError = computed(() => {
    const ctrl = this.control();
    if (!ctrl || !(ctrl.dirty || ctrl.touched) || !ctrl.errors) return null;
    const messages = this.errorMessages();
    for (const key of Object.keys(ctrl.errors)) {
      if (messages[key]) return messages[key];
    }
    return null;
  });
}
